import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, flatMap, switchMap } from 'rxjs/operators';
import { CalcTypeService } from '../../lookups/calc-type.service';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    IStatusMessage,
    ResponseStatus
} from '../../shared';
import { ContractCumulativeService } from '../contract-cumulative.service';
import * as C_Actions from './contract-cumulative.actions';
import moment from 'moment';

@Injectable()
export class ContractCumulativeEffects {
    constructor(
        private actions$: Actions,
        private service: ContractCumulativeService,
        private calcTypeService: CalcTypeService
    ) {}

    getSessionDates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.getSessionDates),
            switchMap(a =>
                this.service.getSessionDates().pipe(
                    flatMap(resp => [
                        C_Actions.loadSessionDates({
                            sessionDates: resp
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    getUsers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.getUsers),
            switchMap(a =>
                this.service.getUsers(a.UserFilterID, a.ShowInactive).pipe(
                    flatMap(resp => [
                        C_Actions.loadUsers({
                            users: resp
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    getTxnTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.getTxnTypes),
            switchMap(() =>
                this.calcTypeService.getCalcType('').pipe(
                    flatMap(resp => [
                        C_Actions.loadTxnTypes({
                            txnTypes: resp
                        })
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    getContractCums$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.getContractCumulatives),
            switchMap(a =>
                this.service.getContractCums().pipe(
                    flatMap(resp => [
                        C_Actions.loadContractCumulatives({
                            contractCumulatives: resp
                        }),
                        new fromShared.Errors.RemoveErrors(
                            resp
                                .filter(
                                    x =>
                                        x.Status.StatusCode ===
                                        ResponseStatus.Success
                                )
                                .map(x => x.Txnid.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    postContractCums$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.postContractCumulatives),
            switchMap(a =>
                this.service
                    .postContractCums(
                        a.DirectOrIndirect,
                        a.Todayonly,
                        a.Asofdate,
                        a.Tasks,
                        a.ServiceItems,
                        a.DirectReports,
                        a.Txntype,
                        a.CostCenters
                    )
                    .pipe(
                        flatMap(resp => [
                            C_Actions.loadContractCumulatives({
                                contractCumulatives: resp
                            }),
                            new fromShared.Errors.RemoveErrors(
                                resp
                                    .filter(
                                        x =>
                                            x.Status.StatusCode ===
                                            ResponseStatus.Success
                                    )
                                    .map(x => x.Txnid.toString())
                            ),
                            new fromShared.Errors.SetErrors(
                                [
                                    ...resp
                                        .filter(
                                            a =>
                                                a.Status.StatusCode >=
                                                DisplayMessageMinimum
                                        )
                                        .map(a => a.Status)
                                ].filter(fromShared.Errors.filterErrors)
                            )
                        ]),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        );
    });

    uploadCumulatives$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(C_Actions.uploadCumulatives),
            switchMap(a =>
                this.service.uploadCumulatives(a.JsonString).pipe(
                    flatMap(resp => [
                        C_Actions.getSessionDates(),
                        C_Actions.postContractCumulatives({
                            DirectOrIndirect: 'Direct',
                            Todayonly: false,
                            Asofdate: JSON.parse(resp)[0].asofdate,
                            Tasks: null,
                            ServiceItems: null,
                            DirectReports: null,
                            Txntype: null,
                            CostCenters: null
                        })
                        // new fromShared.Errors.RemoveErrors(
                        //     resp
                        //         .filter(
                        //             x =>
                        //                 x.Status.StatusCode ===
                        //                 ResponseStatus.Success
                        //         )
                        //         .map(x => x.Txnid.toString())
                        // ),
                        // new fromShared.Errors.SetErrors(
                        //     [
                        //         ...resp
                        //             .filter(
                        //                 a =>
                        //                     a.Status.StatusCode >=
                        //                     DisplayMessageMinimum
                        //             )
                        //             .map(a => a.Status)
                        //     ].filter(fromShared.Errors.filterErrors)
                        // )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    putContractCums$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C_Actions.putContractCumulatives),
            switchMap(a =>
                this.service.putContractCums(a.contractcums).pipe(
                    flatMap(c => [
                        C_Actions.updateContractCumulatives({
                            contractCumulatives: [
                                {
                                    id: a.contractcums
                                        .map(x => x.Txnid)
                                        .toString(),
                                    changes: c.Items[0]
                                }
                            ]
                        }),
                        new fromShared.Errors.RemoveErrors(
                            c.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.Txnid.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...c.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...c.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    deleteContractCums$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C_Actions.removeContractCum),
            switchMap(a =>
                this.service.deleteContractCum(a.ids).pipe(
                    flatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  C_Actions.deleteContractCumulatives({
                                      ids: resp.Items.map(
                                          x => x.EntityID as string
                                      )
                                  })
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
